import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { db } from '../firebase';
import { doc, getDoc } from 'firebase/firestore';

/**
 * Dynamic SEO Component
 * @param {string} pageId - The ID of the page (home, shop, about, etc.)
 * @param {Object} manualData - Override data for specific items (like single product/blog)
 */
export default function SEO({ pageId, manualData }) {
    const [seoConfig, setSeoConfig] = useState(null);
    const [generalConfig, setGeneralConfig] = useState(null);

    useEffect(() => {
        async function loadConfig() {
            try {
                // Fetch both SEO and General config for fallbacks
                const [seoSnap, genSnap] = await Promise.all([
                    getDoc(doc(db, "siteConfig", "seo")),
                    getDoc(doc(db, "siteConfig", "general"))
                ]);
                
                if (seoSnap.exists()) setSeoConfig(seoSnap.data().pages || {});
                if (genSnap.exists()) setGeneralConfig(genSnap.data());
            } catch (err) {
                console.error("SEO Load Error:", err);
            }
        }
        loadConfig();
    }, []);

    // 1. Determine base data from config or fallback
    const pageData = seoConfig?.[pageId] || seoConfig?.['default'] || {};
    const siteName = generalConfig?.siteName || "LB Books";
    const defaultTitle = generalConfig?.siteTitle || siteName;

    // 2. Compute final values (Manual overrides > Page Config > Defaults)
    const finalTitle = manualData?.title || pageData.title || defaultTitle;
    const finalDescription = manualData?.description || pageData.description || generalConfig?.footerDescription || "";
    const finalKeywords = manualData?.keywords || pageData.keywords || "";
    const finalImage = manualData?.image || generalConfig?.siteLogo || "";

    // 3. Prevent duplicate site name in title if already present
    const displayTitle = finalTitle.includes(siteName) ? finalTitle : `${finalTitle} | ${siteName}`;

    return (
        <Helmet>
            {/* Standard Meta Tags */}
            <title>{displayTitle}</title>
            <meta name="description" content={finalDescription} />
            <meta name="keywords" content={finalKeywords} />

            {/* Open Graph / Facebook */}
            <meta property="og:type" content="website" />
            <meta property="og:title" content={displayTitle} />
            <meta property="og:description" content={finalDescription} />
            <meta property="og:image" content={finalImage} />
            {manualData?.price && (
                <meta property="product:price:amount" content={parseFloat(manualData.price).toFixed(2)} />
            )}
            {manualData?.price && (
                <meta property="product:price:currency" content={manualData.currency || "USD"} />
            )}

            {/* Twitter */}
            <meta name="twitter:card" content="summary_large_image" />
            <meta name="twitter:title" content={displayTitle} />
            <meta name="twitter:description" content={finalDescription} />
            <meta name="twitter:image" content={finalImage} />
        </Helmet>
    );
}
